import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'
import Panel from '../../Panel/Panel'
import Badge from '../../Badge/Badge'
import Button from '../../Button/Button'
import Icon from '../../Icons/Icon'
import Notification from '../../../utilities/Notification'
import { Style } from '../../../styles'
import { AppState } from '../../../store'
import { showUserWorkspaceSettingModal } from '../../../store/modals/actions'
import { ModalsActionTypes } from '../../../store/modals/types'
import { UserWorkspaceSettingsController } from '../../../controllers'
import { UserWorkspaceSetting, UserWorkspaceSettingRole } from '../../../types'
import { TabProps } from './GeneralTab'

const PanelTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${Style.spacing.x1};
`

const Days = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Style.spacing.x1_5};
`

const Day = styled.div`
  display: grid;
  grid-template-columns: 112px 1fr;
  align-items: start;
  gap: ${Style.spacing.x1};
`

const DayName = styled.div<{ disabled?: boolean }>`
  font-weight: 500;

  ${props => props.disabled && css`
    color: #637381;
    text-decoration: line-through;
  `}
`

const Hours = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Style.spacing.x0_5};
`

const Unavailable = styled.div`
  color: #637381;
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Style.spacing.x1};
  margin-top: ${Style.spacing.x2};
  padding-top: ${Style.spacing.x2};
  border-top: 1px solid ${Style.color.border};
`

const Timezone = styled.div`
  display: flex;
  align-items: center;
  gap: ${Style.spacing.x1};

  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }
`

interface IProps extends TabProps {
  onScheduleUpdated: () => void
}

// week_schedule is Monday-first
// Extraction hints for `yarn translations` (dynamic keys can't be parsed)
// i18n.t('Scheduling::Monday')
// i18n.t('Scheduling::Tuesday')
// i18n.t('Scheduling::Wednesday')
// i18n.t('Scheduling::Thursday')
// i18n.t('Scheduling::Friday')
// i18n.t('Scheduling::Saturday')
// i18n.t('Scheduling::Sunday')
const DAY_KEYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
// Display order Sunday..Saturday like the reference screenshots
const DISPLAY_ORDER = [6, 0, 1, 2, 3, 4, 5]

export default function AvailabilityTab({ draft, onScheduleUpdated }: IProps) {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch() as (action: ModalsActionTypes) => void
  const currentUserSetting = useSelector<AppState, UserWorkspaceSetting | undefined>(s => s.authentication.currentUser?.user_workspace_setting)
  const [loading, setLoading] = React.useState(false)
  const schedule = draft.week_schedule || []

  // The user workspace setting endpoints (and therefore the modal) are owner-only
  const canEdit = currentUserSetting?.role === UserWorkspaceSettingRole.OWNER && !!draft.host?.id

  const fmt = (hhmm?: string) => {
    if (!hhmm) return ''
    const [h, m] = hhmm.split(':').map(Number)
    return new Intl.DateTimeFormat(i18n.language, { hour: 'numeric', minute: '2-digit' }).format(new Date(2000, 0, 1, h, m))
  }

  const editAvailability = async () => {
    if (!draft.host?.id) return
    setLoading(true)
    try {
      let settingId = currentUserSetting?.user_id === draft.host.id ? currentUserSetting.id : undefined
      if (!settingId) {
        const res = await UserWorkspaceSettingsController.getUserWorkspaceSettings({ user_id: draft.host.id, per_page: 1 })
        settingId = res?.user_workspace_settings?.[0]?.id
      }
      if (!settingId) { Notification.notifyError(t('Scheduling::Could not open availability')); return }
      dispatch(showUserWorkspaceSettingModal({
        userWorkspaceSetting: { id: settingId },
        activeTab: 'working-days',
        onSubmit: () => onScheduleUpdated(),
      }))
    } catch {
      Notification.notifyError(t('Scheduling::Could not open availability'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Panel
      title={
        <PanelTitle>
          {t('Scheduling::Working hours')}
          <Badge type='grey' text={t('Scheduling::Default')} />
        </PanelTitle>
      }
    >
      <Days>
        {DISPLAY_ORDER.map(index => {
          const day = schedule[index]
          const enabled = !!day?.enabled

          return (
            <Day key={index}>
              <DayName disabled={!enabled}>{t(`Scheduling::${DAY_KEYS[index]}`)}</DayName>
              {enabled ? (
                <Hours>
                  {day.break_enabled ? (
                    <>
                      <span>{fmt(day.start_time)} – {fmt(day.break_start_time)}</span>
                      <span>{fmt(day.break_end_time)} – {fmt(day.end_time)}</span>
                    </>
                  ) : (
                    <span>{fmt(day.start_time)} – {fmt(day.end_time)}</span>
                  )}
                </Hours>
              ) : (
                <Unavailable>{t('Scheduling::Unavailable')}</Unavailable>
              )}
            </Day>
          )
        })}
      </Days>

      <Footer>
        <Timezone>
          <Icon icon='globe' />
          {draft.timezone}
        </Timezone>
        {canEdit && (
          <Button
            type='default'
            icon='external-link'
            text={t('Scheduling::Edit availability')}
            onClick={editAvailability}
            disabled={loading}
          />
        )}
      </Footer>
    </Panel>
  )
}
